import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FlaskConical, Layers, KeyRound } from 'lucide-react';
import { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

const ResultList = ({ items, render, testid }) => (
  <ul className="mt-2 space-y-1 text-xs" data-testid={testid}>
    {items.map((it, i) => <li key={i} className="p-2 bg-white rounded border border-slate-100">{render(it)}</li>)}
  </ul>
);

const LmsTestDataCard = () => {
  const [running, setRunning] = useState(null);
  const [testData, setTestData] = useState(null);
  const [slabs, setSlabs] = useState(null);
  const [fps, setFps] = useState(null);

  const run = async (key, path, dryRun, setter) => {
    setRunning(key);
    try {
      const res = await axios.post(`${API_URL}/api/data-repair/${path}`, { dry_run: dryRun }, { withCredentials: true, timeout: 60000 });
      setter(res.data);
      toast.success(dryRun ? 'Preview ready' : 'Done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    } finally {
      setRunning(null);
    }
  };

  const loadFps = async () => {
    setRunning('fp');
    try {
      const res = await axios.get(`${API_URL}/api/data-repair/lms-key-fingerprints`, { withCredentials: true });
      setFps(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not load fingerprints'); }
    finally { setRunning(null); }
  };

  return (
    <div className="card mt-6" data-testid="lms-test-data-card">
      <h3 className="text-lg font-semibold text-slate-900 mb-1">LMS V18 go-live setup</h3>
      <p className="text-sm text-slate-500 mb-4">One-time, idempotent actions for the joint OPS ↔ LMS test plan. Safe to re-run after every deploy.</p>

      <div className="p-4 rounded-lg bg-violet-50 border border-violet-100">
        <div className="flex items-start gap-3">
          <FlaskConical className="w-5 h-5 text-violet-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm">Create LMS test coupons + test learners</div>
            <p className="text-xs text-slate-600 mt-1">
              Creates/refreshes <span className="font-mono">LMSTEST20 · LMSMIN3000 · LMSBABY100 · LMSUSD5 · LMSOLD</span> exactly per the LMS §5 spec, plus the two
              <span className="font-mono"> *@v17test.example.com</span> test learners (flagged as test → excluded from dashboards).
            </p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => run('td_dry', 'lms-v18-test-data', true, setTestData)} disabled={!!running}
                className="px-3 py-1.5 text-sm font-medium bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 disabled:opacity-50" data-testid="lms-testdata-dryrun-btn">
                {running === 'td_dry' ? 'Checking…' : 'Preview'}
              </button>
              <button onClick={() => run('td', 'lms-v18-test-data', false, setTestData)} disabled={!!running}
                className="px-3 py-1.5 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50" data-testid="lms-testdata-create-btn">
                {running === 'td' ? 'Creating…' : 'Create all test data'}
              </button>
            </div>
            {testData && (
              <div data-testid="lms-testdata-result">
                <div className="mt-3 text-xs font-semibold text-slate-700">Coupons {testData.dry_run ? '(preview)' : ''}</div>
                <ResultList testid="lms-testdata-coupons" items={testData.coupons} render={(c) => (
                  <span><span className="font-mono font-semibold">{c.coupon_code}</span> · {c.currency} · <span className="uppercase text-violet-700">{c.action}</span> · ends {c.end_date} — <span className="text-slate-500">{c.purpose}</span></span>
                )} />
                <div className="mt-3 text-xs font-semibold text-slate-700">Test learners</div>
                <ResultList testid="lms-testdata-learners" items={testData.test_learners} render={(l) => (
                  <span><span className="font-mono">{l.email}</span> · tier {l.lms_tier} · <span className="uppercase text-violet-700">{l.action}</span></span>
                )} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 rounded-lg bg-sky-50 border border-sky-100 mt-4">
        <div className="flex items-start gap-3">
          <KeyRound className="w-5 h-5 text-sky-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm">Key fingerprints (deploy-day cross-check with LMS)</div>
            <p className="text-xs text-slate-600 mt-1">First 8 + last 4 characters only — never the full secret. Paste the <span className="font-semibold">outbound pair</span> into LMS production <span className="font-mono">OPS_INBOUND_WEBHOOK_SECRET</span> / <span className="font-mono">OPS_INBOUND_SERVICE_KEY</span> and compare with LMS Launch Readiness.</p>
            <button onClick={loadFps} disabled={!!running} className="mt-3 px-3 py-1.5 text-sm font-medium bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50" data-testid="lms-fingerprints-btn">
              {running === 'fp' ? 'Loading…' : 'Show fingerprints'}
            </button>
            {fps && (
              <div className="mt-3 space-y-2 text-xs" data-testid="lms-fingerprints-result">
                <div className="font-semibold text-slate-700">Outbound pair (what OPS signs LMS calls with)</div>
                <ResultList testid="lms-fp-outbound" items={Object.entries(fps.outbound_pair_ops_signs_with)} render={([k, v]) => <span className="font-mono">{k} = {v}</span>} />
                <div className="font-semibold text-slate-700 mt-2">Inbound accept slots (what OPS accepts from LMS)</div>
                <ResultList testid="lms-fp-inbound" items={Object.entries(fps.inbound_accept_slots)} render={([k, v]) => <span className="font-mono">{k} = {v}</span>} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 rounded-lg bg-amber-50 border border-amber-100 mt-4">
        <div className="flex items-start gap-3">
          <Layers className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm">Normalize till-delivery slabs (LMS A1 / A2)</div>
            <p className="text-xs text-slate-600 mt-1">First slab starts at 1 month, last slab open-ended (9+ months keep the top discount), no overlaps. Applies to every active pricing row (INR + USD).</p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => run('sl_dry', 'normalize-till-delivery-slabs', true, setSlabs)} disabled={!!running}
                className="px-3 py-1.5 text-sm font-medium bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 disabled:opacity-50" data-testid="lms-slabs-dryrun-btn">
                {running === 'sl_dry' ? 'Checking…' : 'Preview'}
              </button>
              <button onClick={() => run('sl', 'normalize-till-delivery-slabs', false, setSlabs)} disabled={!!running}
                className="px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50" data-testid="lms-slabs-apply-btn">
                {running === 'sl' ? 'Applying…' : 'Apply'}
              </button>
            </div>
            {slabs && (
              <ResultList testid="lms-slabs-result" items={slabs.rows} render={(r) => (
                <span><span className="font-semibold">{r.currency}</span> · {r.changed ? <span className="text-amber-700">changed</span> : <span className="text-emerald-700">already normalized</span>} → {r.after.map((s) => `${s.min_months}–${s.max_months ?? '∞'}: ${s.discount_pct}%`).join(' · ')}</span>
              )} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LmsTestDataCard;
